import { useState, useEffect, useRef, useCallback } from "react";
import Modal from "../../layouts/Modal";
import useCategories from "../../data/useCategories";
import usePartMutations from "../../data/usePartMutations";
import { IPartCategory } from "../../models/Part";
import { gtWidgetURL } from "../../config";

export type DiagramMode =
  | { type: "view"; zoneId: string; ucid: string }
  | { type: "findZone"; zoneId: string }
  | { type: "findAll" };

interface PartDiagramModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  umc: string;
  category: IPartCategory;
  mode: DiagramMode;
  partDataId?: string | null;
}

function buildIframeSrc(umc: string, mode: DiagramMode, selectedZone: string | null) {
  const params = new URLSearchParams({ model: umc });

  if (mode.type === "view") {
    params.set("mode", "vehicle");
    params.set("zone", mode.zoneId);
    params.set("parts", mode.ucid);
  } else if (mode.type === "findZone") {
    params.set("mode", "vehicle");
    params.set("zone", mode.zoneId);
  } else if (selectedZone) {
    params.set("mode", "vehicle");
    params.set("zone", selectedZone);
  } else {
    params.set("mode", "boardnav");
  }

  return `${gtWidgetURL}/embed.html?${params.toString()}`;
}

export default function PartDiagramModal({
  open,
  setOpen,
  umc,
  category,
  mode,
  partDataId,
}: PartDiagramModalProps) {
  const { update } = useCategories();
  const { patchPart } = usePartMutations();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [selectedZone, setSelectedZone] = useState<string | null>(
    mode.type !== "findAll" ? mode.zoneId : null
  );
  const [clickedPart, setClickedPart] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSelectedZone(mode.type !== "findAll" ? mode.zoneId : null);
    setClickedPart(null);
    setSaved(false);
  }, [open, mode]);

  const handleMessage = useCallback((e: MessageEvent) => {
    if (e.origin !== gtWidgetURL) return;
    const data = e.data;
    if (!data || !data.type) return;

    if (data.type === "zoneSelected") {
      setSelectedZone(data.zoneId);
      setClickedPart(null);
      setSaved(false);

      iframeRef.current?.contentWindow?.postMessage(
        { type: "navigate", mode: "vehicle", model: umc, zone: data.zoneId },
        gtWidgetURL
      );
    } else if (data.type === "partClicked") {
      setClickedPart(data.partCode);
      setSaved(false);
    }
  }, [umc]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [open, handleMessage]);

  const showBoardNav = mode.type === "findAll" && selectedZone == null;
  const canSave = mode.type !== "view" && (clickedPart || selectedZone);

  const handleBack = () => {
    setSelectedZone(null);
    setClickedPart(null);

    iframeRef.current?.contentWindow?.postMessage(
      { type: "navigate", mode: "boardnav", model: umc },
      gtWidgetURL
    );
  };

  const handleSave = () => {
    let newUcid = category.ucid;
    if (clickedPart) {
      const existing = (category.ucid || "").split(",").map(s => s.trim()).filter(Boolean);
      if (!existing.includes(clickedPart)) {
        newUcid = existing.length ? `${existing.join(", ")}, ${clickedPart}` : clickedPart;
      }
    }

    const updated: IPartCategory = {
      ...category,
      ucidZone: selectedZone ?? category.ucidZone,
      ucid: newUcid,
    };

    update.mutate(updated, {
      onSuccess: () => {
        if (partDataId && clickedPart) {
          patchPart.mutate({ id: partDataId, matchedUcid: clickedPart });
        }
        setSaved(true);
      },
    });
  };

  const iframeSrc = open ? buildIframeSrc(umc, mode, selectedZone) : "";

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-7xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {mode.type === "view" ? "Part Diagram" : "Find Part Diagram"}
            </h3>
            <p className="text-sm text-gray-500">
              {category.name}
              {selectedZone && (
                <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                  Zone: {selectedZone}
                </span>
              )}
              {clickedPart && (
                <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                  Part: {clickedPart}
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {canSave && !saved && (
              <button
                type="button"
                onClick={handleSave}
                disabled={update.isLoading}
                className="text-sm px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
              >
                {update.isLoading ? "Saving..." : clickedPart ? "Save Zone & Part" : "Save Zone"}
              </button>
            )}
            {saved && (
              <span className="text-sm text-green-600 font-medium">Saved</span>
            )}
            {selectedZone && (
              <button
                type="button"
                onClick={handleBack}
                className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Back to Zones
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm px-3 py-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden bg-white min-h-[450px]">
          {showBoardNav && (
            <p className="text-sm text-gray-500 px-4 pt-4">Select a zone on the vehicle to view parts.</p>
          )}
          {open && (
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              title="GT Motive Part Diagrams"
              className="w-full border-0"
              style={{ height: 500 }}
              allow="cross-origin-isolated"
            />
          )}
        </div>

        {!showBoardNav && (
          <div className="flex items-center gap-x-5 text-xs text-gray-500">
            <div className="flex items-center gap-x-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#D4A017" }} />
              <span>Matched OEM part code</span>
            </div>
            <div className="flex items-center gap-x-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#c8e6c5" }} />
              <span>Pending — OEM part number not yet known</span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
